import { useState, useEffect } from "react";
import { useAuth, API } from "../App";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";
import { 
  Loader2, Shield, Eye, EyeOff, Trash2, RefreshCw,
  AlertTriangle, CheckCircle, Filter, Newspaper
} from "lucide-react";
import { PostCard } from "./SocialFeed";
import axios from "axios";
import { toast } from "sonner";
import { formatDistanceToNow, parseISO } from "date-fns";

/**
 * Moderation Panel for Org Admins
 * Allows viewing all posts, hiding/unhiding, and deleting inappropriate content
 */
const ModerationPanel = ({ orgId, className = "" }) => {
  const { token, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [posts, setPosts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [hiddenCount, setHiddenCount] = useState(0);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    if (orgId) {
      fetchPosts();
    }
  }, [orgId, filterType]);

  const fetchPosts = async (loadMore = false) => {
    if (!loadMore) setLoading(true);
    
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(
        `${API}/social/orgs/${orgId}/moderation?page=${loadMore ? page + 1 : 1}&page_size=15&filter_type=${filterType}`,
        { headers }
      );
      
      if (loadMore) {
        setPosts([...posts, ...(res.data.posts || [])]);
        setPage(page + 1);
      } else {
        setPosts(res.data.posts || []);
        setPage(1);
      }
      
      setHiddenCount(res.data.hidden_count || 0);
      setHasMore(res.data.page < res.data.pages);
    } catch (error) {
      console.error("Failed to fetch moderation queue:", error);
      toast.error("Failed to load posts");
    } finally {
      setLoading(false);
    }
  };

  const handleHide = async (postId) => {
    setActionLoading(postId);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/social/posts/${postId}/hide`, {}, { headers });
      
      setPosts(posts.map(p => 
        p.post_id === postId 
          ? { ...p, is_hidden: true, hidden_at: new Date().toISOString(), hidden_by: user.user_id }
          : p
      ));
      setHiddenCount(hiddenCount + 1);
      toast.success("Post hidden from public view");
    } catch (error) {
      toast.error("Failed to hide post");
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnhide = async (postId) => {
    setActionLoading(postId);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/social/posts/${postId}/unhide`, {}, { headers });
      
      setPosts(posts.map(p => 
        p.post_id === postId 
          ? { ...p, is_hidden: false, hidden_at: null, hidden_by: null }
          : p
      ));
      setHiddenCount(Math.max(0, hiddenCount - 1));
      toast.success("Post restored to public view");
    } catch (error) {
      toast.error("Failed to unhide post");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    
    setActionLoading(deleteTarget);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/social/orgs/${orgId}/posts/${deleteTarget}`, { headers });
      
      const wasHidden = posts.find(p => p.post_id === deleteTarget)?.is_hidden;
      setPosts(posts.filter(p => p.post_id !== deleteTarget));
      if (wasHidden) setHiddenCount(Math.max(0, hiddenCount - 1));
      
      toast.success("Post permanently deleted");
    } catch (error) {
      toast.error("Failed to delete post");
    } finally {
      setActionLoading(null);
      setDeleteTarget(null);
    }
  };

  if (loading && posts.length === 0) {
    return (
      <div className={`flex items-center justify-center h-64 ${className}`}>
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`} data-testid="moderation-panel">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center">
            <Shield className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Content Moderation</h3>
            <p className="text-sm text-gray-500">
              {posts.length} posts • {hiddenCount} hidden
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={() => fetchPosts()}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Filter Tabs */}
      <Tabs value={filterType} onValueChange={setFilterType} className="w-full">
        <TabsList className="bg-gray-100 p-1 rounded-xl">
          <TabsTrigger value="all" className="rounded-lg">
            <Newspaper className="w-4 h-4 mr-2" />
            All Posts
          </TabsTrigger>
          <TabsTrigger value="hidden" className="rounded-lg">
            <EyeOff className="w-4 h-4 mr-2" />
            Hidden ({hiddenCount})
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Posts List */}
      {posts.length > 0 ? (
        <div className="space-y-4">
          {posts.map((post) => (
            <div 
              key={post.post_id} 
              className={`relative ${post.is_hidden ? 'opacity-60' : ''}`}
            >
              {/* Hidden Badge */}
              {post.is_hidden && (
                <div className="absolute -top-2 -left-2 z-10">
                  <Badge className="bg-amber-100 text-amber-700 border border-amber-200">
                    <EyeOff className="w-3 h-3 mr-1" />
                    Hidden
                  </Badge>
                </div>
              )}
              
              {/* Post Card */}
              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
                <PostCard
                  post={post}
                  onLike={() => {}}
                  onComment={() => {}}
                  showAnalytics={false}
                  currentUserId={user?.user_id}
                />
                
                {/* Moderation Actions */}
                <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    {post.is_hidden && post.hidden_at && (
                      <span>
                        Hidden {formatDistanceToNow(parseISO(post.hidden_at), { addSuffix: true })}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {post.is_hidden ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleUnhide(post.post_id)}
                        disabled={actionLoading === post.post_id}
                      >
                        {actionLoading === post.post_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <Eye className="w-4 h-4 mr-1" />
                            Unhide
                          </>
                        )}
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleHide(post.post_id)}
                        disabled={actionLoading === post.post_id}
                      >
                        {actionLoading === post.post_id ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <EyeOff className="w-4 h-4 mr-1" />
                            Hide
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setDeleteTarget(post.post_id)}
                      disabled={actionLoading === post.post_id}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
          
          {hasMore && (
            <div className="text-center pt-4">
              <Button variant="outline" onClick={() => fetchPosts(true)}>
                Load More Posts
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 text-center">
          <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
          <h4 className="text-lg font-semibold text-gray-700 mb-2">
            {filterType === "hidden" ? "No Hidden Posts" : "No Posts to Moderate"}
          </h4>
          <p className="text-gray-500">
            {filterType === "hidden" 
              ? "All posts are currently visible to users."
              : "There are no posts in this organization yet."
            }
          </p>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Delete Post Permanently?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The post, all comments, and likes will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {actionLoading ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Trash2 className="w-4 h-4 mr-2" />
              )}
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ModerationPanel;
